import React from 'react'
import { connect } from 'react-redux'
import { getFormValues } from 'redux-form'
import { compose, withHandlers, withProps, withState, lifecycle } from 'recompose'
import withType from 'components/common/hoc/withType'
import withNormalizeValue from 'components/common/hoc/withNormalizeValue'
import reduxForm from 'components/common/hoc/withFormErrorFocus'
import { normalizeDate } from 'helpers/time-format'
import BOTContactUsForm from './BOTContactUsForm'
import formViewModel from '../viewModel'
import { submitCommentAction } from '../actions'
import { contactValidator as validate } from '../validate'
import {
  CONTACT_US_FORM,
  FUTURE_CONTACT,
  MAX_COMMENT_LENGTH,
  SUBJECT_FIELD,
  SUBJECT_FIELDS,
  DESTINATION_EMAIL,
  DATE_OF_SERVICE,
  STORE_EXPERIENCE_NAME,
  STORE_LOCATION,
  REQUIRED_FIELDS_MESSAGE,
  CONTACT_REQUIRED_FIELDS,
  RECAPTCHA_ERROR_MESSAGE
} from '../constants'

const withFormProps = withProps({
  formViewModel,
  initialValues: {
    [FUTURE_CONTACT]: true
  },
  captchaRef: React.createRef()
})

const withReCaptchaErrorMessage = withState('reCaptchaErrorMessage', 'setReCaptchaErrorMessage', null)

const withFormHandlers = withHandlers({
  onSubmit: ({ submitCommentAction, captchaRef, setReCaptchaErrorMessage }) => (
    {
      [SUBJECT_FIELD]: subject,
      [DATE_OF_SERVICE]: date,
      ...values
    }) => {
    const captchaValue = captchaRef.current.getValue()

    if (!captchaValue) {
      setReCaptchaErrorMessage(RECAPTCHA_ERROR_MESSAGE)
    } else {
      setReCaptchaErrorMessage(null)
      submitCommentAction({
        [DATE_OF_SERVICE]: normalizeDate(date),
        [DESTINATION_EMAIL]: SUBJECT_FIELDS[subject],
        subject,
        ...values
      })
      captchaRef.current.reset()
    }
  },
  normalizeCommentField: () => value => value.substring(0, MAX_COMMENT_LENGTH)
})

const withSetStoreLocationRequired = withState('storeLocationRequired', 'setStoreLocationRequired', false)

const withStoreLocationRequiredProp = withProps(({ storeLocationRequired }) => ({
  storeLocationRequiredProp: storeLocationRequired ? { required: true, label: 'Store Address*' } : {}
}))

const withValidateSubject = withHandlers({
  handleSubjectSelect: ({ setStoreLocationRequired }) => ({ value }) =>
    setStoreLocationRequired(value === STORE_EXPERIENCE_NAME)
})

const mapStateToProps = state => {
  const values = getFormValues(CONTACT_US_FORM)(state)
  return {
    allRequiredFilled: CONTACT_REQUIRED_FIELDS.every(field => values[field]),
    storeAddressIsFilled: !!values[STORE_LOCATION]
  }
}

const withSetErrorMessage = withState('error', 'setErrorMessage', undefined)

const withValidateRequiredFields = withHandlers({
  validateRequiredFields: ({ allRequiredFilled, storeAddressIsFilled, storeLocationRequired, setErrorMessage }) => () =>
    !allRequiredFilled || (storeLocationRequired && !storeAddressIsFilled) ?
      setErrorMessage(REQUIRED_FIELDS_MESSAGE) : setErrorMessage(undefined)
})

const withLifecycle = lifecycle({
  componentDidMount() {
    const { validateRequiredFields } = this.props
    validateRequiredFields()
  },
  componentDidUpdate({
    allRequiredFilled: prevAllRequiredFilled,
    storeAddressIsFilled: prevStoreAddressIsFilled,
    storeLocationRequired: prevStoreLocationRequired
  }) {
    const { allRequiredFilled, storeAddressIsFilled, storeLocationRequired, validateRequiredFields } = this.props
    if (prevAllRequiredFilled !== allRequiredFilled ||
      prevStoreAddressIsFilled !== storeAddressIsFilled ||
      prevStoreLocationRequired !== storeLocationRequired) {
      validateRequiredFields()
    }
  }
})

export default compose(
  withType({ type: 'BOTContactUsForm' }),
  connect(null, { submitCommentAction }),
  withReCaptchaErrorMessage,
  withFormProps,
  withFormHandlers,
  withNormalizeValue,
  withSetStoreLocationRequired,
  withValidateSubject,
  withStoreLocationRequiredProp,
  reduxForm({ form: CONTACT_US_FORM, validate }),
  connect(mapStateToProps),
  withSetErrorMessage,
  withValidateRequiredFields,
  withLifecycle
)(BOTContactUsForm)
